import { ReactNode, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { AppSidebar } from "./AppSidebar";
import { AppHeader } from "./AppHeader";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { PartnerThemeProvider } from "@/contexts/PartnerThemeContext";
import { useAppShellTranslations } from "@/components/app/i18n/shell";
import { UiLabelsProvider, type UiLabels } from "@/components/ui/ui-labels";
import { MobileSidebarDrawer } from "./sidebar/MobileSidebarDrawer";
import { useMobileSidebar } from "./sidebar/useMobileSidebar";

interface AppLayoutProps {
  children: ReactNode;
}

function MainContent({ children }: { children: ReactNode }) {
  return (
    <main className="relative flex-1 overflow-y-auto bg-tr-canvas">
      <div className="p-4 md:p-7">{children}</div>
    </main>
  );
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user, signOut, tenantId } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const t = useAppShellTranslations();
  const uiLabels = useMemo<UiLabels>(() => ({ close: t.a11y.close }), [t.a11y.close]);
  const [mobileMenuOpen, setMobileMenuOpen] = useMobileSidebar();

  // Fetch tenant data
  const { data: tenant } = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: async () => {
      if (!tenantId) return null;
      const { data, error } = await supabase
        .from("tenants")
        .select("*")
        // tenants.id é numérico; PostgREST recebe o mesmo `id=eq.<n>` de antes
        .eq("id", Number(tenantId))
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!tenantId,
  });

  // Fetch user profile for display name
  const { data: profile } = useQuery({
    queryKey: ["my-profile-header", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, avatar_url")
        .eq("id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: t.layout.logoutSuccessTitle,
        description: t.layout.logoutSuccessDescription,
      });
      navigate("/login");
    } catch (error) {
      toast({
        title: t.layout.logoutErrorTitle,
        description: t.layout.logoutErrorDescription,
        variant: "destructive",
      });
    }
  };

  const userName =
    profile?.full_name ||
    user?.user_metadata?.full_name ||
    user?.email?.split("@")[0] ||
    t.layout.userFallback;
  const userEmail = user?.email || "";
  const userAvatar = profile?.avatar_url || "";
  const tenantName = tenant?.name || t.layout.organizationFallback;
  const sidebarProps = { tenantName, userName, userEmail, userAvatar, onSignOut: handleSignOut };
  return (
    <PartnerThemeProvider>
      <UiLabelsProvider value={uiLabels}>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="tr-portal flex h-screen w-full bg-tr-canvas">
          {/* Desktop (≥ lg): sidebar fixo 256/72 */}
          <div className="hidden lg:flex">
            <AppSidebar {...sidebarProps} />
          </div>
          {/* Mobile (< lg): drawer 304 px com o mesmo conteúdo e as mesmas permissões */}
          <MobileSidebarDrawer open={mobileMenuOpen} onOpenChange={setMobileMenuOpen} title={t.sidebar.mainNavigation}>
            <AppSidebar {...sidebarProps} variant="drawer" onClose={() => setMobileMenuOpen(false)} />
          </MobileSidebarDrawer>
          <div className="flex flex-col flex-1 overflow-hidden">
            <AppHeader onOpenMenu={() => setMobileMenuOpen(true)} />
            <MainContent>{children}</MainContent>
          </div>
        </div>
      </UiLabelsProvider>
    </PartnerThemeProvider>
  );
}
